/**
 * PDP Size Selector — Interactive behaviour
 * Handles format-split tab switching, unified dropdown toggle,
 * size option selection (in-page variant update), and BYO inline trigger.
 */
(function () {
  'use strict';

  var container = document.querySelector('.pdp-size-selector');
  if (!container) return;

  var mode = container.dataset.displayMode;
  var urlParams = new URLSearchParams(window.location.search);
  var hasByoDefaultIntent = urlParams.get('byo-default') === '1';
  var hasSkipByoDefaultIntent = urlParams.get('skip-byo-default') === '1';
  var currentVariantAvailable = container.dataset.currentVariantAvailable === 'true';
  var currentProductInByo = container.dataset.currentProductInByo === 'true';
  var currentProductHasStandardAvailable = container.dataset.currentProductHasStandardAvailable === 'true';
  var noStandardSizesAvailable = !currentProductHasStandardAvailable;
  var defaultByoOnLoad = !hasSkipByoDefaultIntent && currentProductInByo && !currentVariantAvailable && hasByoDefaultIntent;
  var byoInlines = document.querySelectorAll('.pdp-ss__byo-inline');
  var byoInitialized = {};
  var sizeList = null;
  var unifiedTrigger = null;
  var unifiedPanel = null;
  var isBmsmProduct = container.dataset.isBmsmProduct === 'true';
  var bmsmPanel = container.querySelector('[data-bmsm-panel]');
  var bmsmTargetButtons = bmsmPanel ? Array.from(bmsmPanel.querySelectorAll('[data-bmsm-target]')) : [];
  var bmsmToggle = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-toggle]') : null;
  var bmsmList = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-list]') : null;
  var bmsmMixSummary = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-mix-summary]') : null;
  var bmsmMixFan = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-mix-fan]') : null;
  var bmsmMixLabel = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-mix-label]') : null;
  var bmsmMixPrice = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-mix-price]') : null;
  var bmsmMixDiscount = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-mix-discount]') : null;
  var bmsmOptions = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-options]') : null;
  var bmsmActions = bmsmPanel ? bmsmPanel.querySelector('.pdp-ss__bmsm-actions') : null;
  var bmsmMessage = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-message]') : null;
  var bmsmSubtitle = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-subtitle]') : null;
  var bmsmBadge = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-badge]') : null;
  var bmsmProgressFill = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-progress-fill]') : null;
  var bmsmClearBtn = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-clear]') : null;
  var bmsmHideBtn = bmsmPanel ? bmsmPanel.querySelector('[data-bmsm-hide]') : null;
  var bmsmOptionPriceEls = bmsmPanel ? Array.from(bmsmPanel.querySelectorAll('[data-bmsm-option-price]')) : [];
  var bmsmOptionCompareEls = bmsmPanel ? Array.from(bmsmPanel.querySelectorAll('[data-bmsm-option-compare]')) : [];
  var sizeBmsmBadge = container.querySelector('[data-bmsm-size-badge]');
  var bmsmRows = bmsmList ? Array.from(bmsmList.querySelectorAll('.pdp-ss__bmsm-row')) : [];
  var bmsmState = {
    active: false,
    target: 1,
    expanded: false,
    userCollapsed: false,
    quantities: {},
    activeVariantId: '',
    submitting: false
  };
  var bmsmBypassInterceptOnce = false;

  function setByoSelectedState(isByoActive) {
    if (!document.body) return;
    document.body.classList.toggle('byo-selected', !!isByoActive);
  }

  var initActiveOpt = container.querySelector('.pdp-ss__size-option--active, .pdp-ss__dropdown-option--active');
  setByoSelectedState(!!(initActiveOpt && initActiveOpt.dataset && initActiveOpt.dataset.isByo === 'true'));

  function clearByoDefaultIntent() {
    if (!hasByoDefaultIntent && !hasSkipByoDefaultIntent) return;
    if (hasByoDefaultIntent) urlParams.delete('byo-default');
    if (hasSkipByoDefaultIntent) urlParams.delete('skip-byo-default');
    var qs = urlParams.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  }

  function getStickyHeaderHeight() {
    var body = document.body;
    var header = body && body.classList.contains('is-header-sticky')
      ? document.querySelector('.ae--header')
      : null;
    return header ? (header.offsetHeight || 0) : 0;
  }

  function getViewportOffset(extraGap) {
    var gap = typeof extraGap === 'number' ? extraGap : 8;
    return getStickyHeaderHeight() + gap;
  }

  function getContainerOffset(extraGap) {
    return typeof extraGap === 'number' ? extraGap : 8;
  }

  function anchorToDropdownTop(el) {
    if (!el) return;
    var isSizeList = el.classList && el.classList.contains('pdp-ss__size-list--dropdown');
    var fieldset = isSizeList ? el.closest('.pdp-ss__fieldset') : null;
    var heading = fieldset ? fieldset.querySelector('.pdp-ss__label') : null;
    var target = heading || fieldset || el;
    var leadIn = isSizeList ? 12 : 12; /* Consistent 12px space above all anchors. */
    var viewportOffset = getViewportOffset(8);
    var containerOffset = getContainerOffset(8);
    var infoContainer = target.closest('.ae--product-information') || document.querySelector('.ae--product-information');

    if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight + 1) {
      var containerRect = infoContainer.getBoundingClientRect();
      var targetRect = target.getBoundingClientRect();
      var localTop = infoContainer.scrollTop + (targetRect.top - containerRect.top) - containerOffset - leadIn;
      infoContainer.scrollTo({ top: Math.max(localTop, 0), behavior: 'smooth' });
      return;
    }

    var top = target.getBoundingClientRect().top + window.pageYOffset - viewportOffset - leadIn;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  function anchorToFieldsetTop(el, extraLeadIn) {
    if (!el) return;
    var leadIn = typeof extraLeadIn === 'number' ? extraLeadIn : 12;
    var viewportOffset = getViewportOffset(8);
    var containerOffset = getContainerOffset(8);
    var infoContainer = el.closest('.ae--product-information') || document.querySelector('.ae--product-information');

    if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight + 1) {
      var containerRect = infoContainer.getBoundingClientRect();
      var targetRect = el.getBoundingClientRect();
      var localTop = infoContainer.scrollTop + (targetRect.top - containerRect.top) - containerOffset - leadIn;
      infoContainer.scrollTo({ top: Math.max(localTop, 0), behavior: 'smooth' });
      return;
    }

    var top = el.getBoundingClientRect().top + window.pageYOffset - viewportOffset - leadIn;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  function _doAnchorToFlavors() {
    var flavorsEl = document.querySelector('.ae--product-flavors');
    if (!flavorsEl) return;
    var leadIn = 16;
    var infoContainer = flavorsEl.closest('.ae--product-information') || document.querySelector('.ae--product-information');
    if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight + 1) {
      var cRect = infoContainer.getBoundingClientRect();
      var tRect = flavorsEl.getBoundingClientRect();
      var localTop = infoContainer.scrollTop + (tRect.top - cRect.top) - leadIn;
      infoContainer.scrollTo({ top: Math.max(localTop, 0), behavior: 'smooth' });
      return;
    }
    var stickyOffset = getStickyHeaderHeight();
    var top = flavorsEl.getBoundingClientRect().top + window.pageYOffset - stickyOffset - leadIn;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  function anchorToFlavorDropdown() {
    /* Double-rAF + setTimeout to ensure the browser has reflowed after expansion */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        _doAnchorToFlavors();
      });
    });
    window.setTimeout(_doAnchorToFlavors, 120);
  }

  function anchorToSelectASize() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return;
    var selectSizeEl = container.querySelector('.select-a-size') || document.querySelector('.select-a-size');
    if (!selectSizeEl) return;
    var stickyOffset = 0;
    if (document.body && document.body.classList.contains('is-header-sticky')) {
      var stickyHeader = document.querySelector('.ae--header');
      stickyOffset = stickyHeader ? (stickyHeader.offsetHeight || 0) : 0;
    }
    var wTop = selectSizeEl.getBoundingClientRect().top + window.pageYOffset - stickyOffset - 12;
    window.scrollTo({ top: Math.max(wTop, 0), behavior: 'auto' });
  }

  function anchorToPageTop() {
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var infoContainer = container.closest('.ae--product-information') || document.querySelector('.ae--product-information');
    var viewportOffset = getViewportOffset(0);

    if (!isMobile) {
      /* Desktop: scroll inside ae--product-information to just above .select-a-size */
      if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight + 1) {
        var selectSizeEl = document.querySelector('.select-a-size');
        if (selectSizeEl) {
          var containerRect = infoContainer.getBoundingClientRect();
          var targetRect = selectSizeEl.getBoundingClientRect();
          var localTop = infoContainer.scrollTop + (targetRect.top - containerRect.top) - 12;
          infoContainer.scrollTo({ top: Math.max(localTop, 0), behavior: 'smooth' });
        } else {
          infoContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      return;
    }

    /* On mobile, scroll to the top of the page. */
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function queueAnchorToPageTop() {
    window.setTimeout(function () {
      anchorToPageTop();
      window.requestAnimationFrame(function () {
        anchorToPageTop();
      });
      window.setTimeout(function () {
        anchorToPageTop();
      }, 260);
    }, 200);
  }

  function closePdpDropdowns(exceptSource) {
    if (sizeList && exceptSource !== 'size-list') {
      /* Only collapse the size list when in single-option mode AND
         that option is already selected — otherwise keep it open so
         the radio button remains visible. */
      if (getVisiblePackOptionCount() <= 1) {
        var soloOpt = Array.from(container.querySelectorAll('.pdp-ss__size-option')).find(function (o) {
          return o.style.display !== 'none';
        });
        var soloSelected = soloOpt && soloOpt.classList.contains('pdp-ss__size-option--active');
        if (soloSelected) {
          sizeList.classList.remove('pdp-ss__size-list--expanded');
        }
      }
    }
    if (unifiedTrigger && unifiedPanel && exceptSource !== 'size-dropdown') {
      unifiedTrigger.setAttribute('aria-expanded', 'false');
      unifiedPanel.style.display = 'none';
    }
  }

  function getVisiblePackOptionCount() {
    var opts = container.querySelectorAll('.pdp-ss__size-option');
    return Array.from(opts).filter(function (opt) {
      return opt.style.display !== 'none';
    }).length;
  }

  function announceDropdownOpened(source) {
    document.dispatchEvent(new CustomEvent('pdp:dropdown-opened', { detail: { source: source } }));
  }

  function announceCloseAllDropdowns(exceptSource) {
    document.dispatchEvent(new CustomEvent('pdp:dropdown-close-all', { detail: { except: exceptSource || '' } }));
  }

  document.addEventListener('pdp:dropdown-opened', function (e) {
    var source = e && e.detail ? e.detail.source : '';
    closePdpDropdowns(source);
  });

  document.addEventListener('pdp:dropdown-close-all', function (e) {
    var except = e && e.detail ? e.detail.except : '';
    closePdpDropdowns(except);
  });

  /* BYO builder can emit flavor-specific media product IDs (single-flavor selection). */
  document.addEventListener('pdp:byo-media-product-changed', function (e) {
    var detail = e && e.detail ? e.detail : null;
    var mediaProductId = detail && detail.productId ? String(detail.productId) : '';
    if (!mediaProductId) return;

    var activeByo = container.querySelector('.pdp-ss__size-option--active[data-is-byo="true"], .pdp-ss__dropdown-option--active[data-is-byo="true"]');
    if (!activeByo) return;

    updateGallery(mediaProductId, true);
  });

  function handleOutsidePdpDropdowns(e) {
    var insidePdpDropdown = e.target.closest('.pdp-ss__size-list--dropdown, .pdp-ss__dropdown, #rc-toggle-panel, #rc-toggle-btn');
    if (!insidePdpDropdown) announceCloseAllDropdowns();
  }

  /* Capture phase prevents other click handlers from blocking outside-close behavior. */
  document.addEventListener('click', handleOutsidePdpDropdowns, true);
  document.addEventListener('touchstart', handleOutsidePdpDropdowns, true);

  function isOptionAvailable(opt) {
    return !!opt && opt.dataset.available !== 'false';
  }

  function syncQtyVisibilityForNotify(isAvailable) {
    var atcRow = document.querySelector('.pdp-atc-row');
    if (atcRow) atcRow.classList.toggle('pdp-atc-row--notify', !isAvailable);

    var qtyWrap = document.querySelector('.pdp-atc-wrap .ae--form-quantity:not(.byo-builder__bundle-qty)');
    if (!qtyWrap) return;

    if (isAvailable) {
      qtyWrap.style.display = 'flex';
      qtyWrap.classList.add('is-active');
    } else {
      qtyWrap.style.display = 'none';
      qtyWrap.classList.remove('is-active');
    }
  }

  function getActiveSizeOption() {
    return container.querySelector('.pdp-ss__size-option--active, .pdp-ss__dropdown-option--active');
  }

  function bmsmNormalizePackageKey(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function bmsmIsTenServingBag(entry) {
    if (!entry) return false;
    var label = bmsmNormalizePackageKey((entry.title || '') + ' ' + (entry.key || ''));
    return /(^|\D)10\s*servings?\b/.test(label) && label.indexOf('stick') === -1;
  }

  function bmsmResolveRowVariant(row) {
    if (!row) return null;
    if (!row._bmsmVariantMap) {
      try {
        row._bmsmVariantMap = JSON.parse(row.dataset.variantMap || '[]');
      } catch (e) {
        row._bmsmVariantMap = [];
      }
    }

    var entries = row._bmsmVariantMap || [];
    if (!entries.length) {
      return {
        variantId: String(row.dataset.variantId || ''),
        available: row.dataset.available !== 'false'
      };
    }

    var picked = entries.find(function (entry) {
      if (!bmsmIsTenServingBag(entry)) return false;
      return entry.bmsm_eligible === true;
    });
    if (!picked) return null;

    return {
      variantId: String(picked.variant_id || ''),
      available: picked.available !== false,
      price: parseInt(picked.price, 10) || 0,
      image: String(picked.image || '')
    };
  }

  function bmsmSyncRowsForTenServingBags() {
    bmsmRows.forEach(function (row) {
      var resolved = bmsmResolveRowVariant(row);
      if (!resolved || !resolved.variantId || !resolved.available) {
        row.dataset.activeVariantId = resolved && resolved.variantId ? resolved.variantId : '';
        row.dataset.activeAvailable = 'false';
        row.style.display = 'none';
        return;
      }
      row.dataset.activeVariantId = resolved.variantId;
      row.dataset.activeAvailable = 'true';
      row.dataset.activePrice = String(resolved.price || 0);
      if (resolved.image) row.dataset.featuredImage = resolved.image;
      row.style.display = '';
    });
  }

  var bmsmDiscount1 = bmsmPanel ? (parseFloat(bmsmPanel.dataset.bmsmDiscountTwo) || 10) : 10;
  var bmsmDiscount2 = bmsmPanel ? (parseFloat(bmsmPanel.dataset.bmsmDiscountThree) || 15) : 15;

  function bmsmGetDiscountRate(target) {
    if (target >= 3) return bmsmDiscount2 / 100;
    if (target >= 2) return bmsmDiscount1 / 100;
    return 0;
  }

  function bmsmFormatMoney(cents) {
    var fmt = window.moneyFormat || '${{amount}}';
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents, fmt);
    return '$' + (cents / 100).toFixed(2);
  }

  function bmsmGetBasePrice() {
    var panelPrice = bmsmPanel ? (parseInt(bmsmPanel.dataset.bmsmUnitPrice, 10) || 0) : 0;
    if (panelPrice) return panelPrice;
    var current = bmsmRows.find(function (row) { return row.dataset.current === 'true'; });
    if (current) return parseInt(current.dataset.activePrice, 10) || 0;
    return 0;
  }

  function syncSizeBmsmBadgeForOption(opt) {
    if (!sizeBmsmBadge) return;
    var isByo = !!(opt && opt.dataset && opt.dataset.isByo === 'true');
    var isTagged = !!(opt && opt.dataset && opt.dataset.isBmsmTagged === 'true');
    sizeBmsmBadge.style.display = (isTagged && !isByo && isOptionAvailable(opt)) ? 'inline-flex' : 'none';
  }

  function bmsmTotalSelected() {
    return Object.keys(bmsmState.quantities).reduce(function (sum, variantId) {
      return sum + (parseInt(bmsmState.quantities[variantId], 10) || 0);
    }, 0);
  }

  function bmsmSetBaseQuantities(currentVariantId) {
    bmsmState.quantities = {};
    if (!currentVariantId) return;
    bmsmState.quantities[currentVariantId] = 1;
  }

  function bmsmPreferredRowVariantId() {
    var current = bmsmRows.find(function (row) { return row.dataset.current === 'true'; });
    if (current && current.dataset.activeAvailable !== 'false' && current.dataset.activeVariantId) {
      return String(current.dataset.activeVariantId);
    }
    var firstVisible = bmsmRows.find(function (row) { return row.style.display !== 'none'; });
    if (firstVisible && firstVisible.dataset.activeVariantId) return String(firstVisible.dataset.activeVariantId);
    return '';
  }

  function bmsmNormalizeToTarget() {
    var target = bmsmState.target;
    var preferredVariantId = bmsmState.activeVariantId || bmsmPreferredRowVariantId();
    if (!preferredVariantId) return;

    /* Set the current page flavor's quantity to match the selected target.
       Other flavors' quantities are left untouched — no cap on total bags. */
    if (target > 0) {
      bmsmState.quantities[preferredVariantId] = target;
    } else {
      delete bmsmState.quantities[preferredVariantId];
    }
  }

  /* Returns the qty input value as a multiplier on the BMSM bundle (min 1). */
  function bmsmGetQtyMultiplier() {
    var qtyInput = document.querySelector('.ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"]');
    return Math.max(parseInt((qtyInput && qtyInput.value) || '1', 10) || 1, 1);
  }

  function bmsmSyncFormInputs() {
    /* The qty picker is a multiplier on the BMSM bundle — leave its value alone.
       Just ensure it is visible. */
    var qtyWrap = document.querySelector('.ae--form-quantity:not(.byo-builder__bundle-qty)');
    if (qtyWrap) qtyWrap.style.display = '';
  }

  function bmsmUpdateAtcPrice() {
    if (!bmsmState.active) return;
    var basePrice = bmsmGetBasePrice();
    if (!basePrice) return;
    /* Total bags = BMSM selection × qty picker multiplier */
    var bmsmQty = Math.max(bmsmTotalSelected(), 1);
    var multiplier = bmsmGetQtyMultiplier();
    var totalQty = bmsmQty * multiplier;
    /* Discount rate is based on the TOTAL bags being added to cart,
       so qty multiplier can unlock higher discount tiers. */
    var discountRate = bmsmGetDiscountRate(totalQty);
    /* Discount one unit first, round up, then multiply by total quantity */
    var unitDiscounted = Math.ceil(basePrice * (1 - discountRate));
    var compareTotal = basePrice * totalQty;
    var discountedTotal = unitDiscounted * totalQty;
    var rcToggle = document.getElementById('rc-toggle-btn');
    var isSubscribed = !!(rcToggle && rcToggle.getAttribute('aria-expanded') === 'true');
    var unitSub = isSubscribed ? Math.ceil(unitDiscounted * 0.85) : unitDiscounted;
    var finalTotal = unitSub * totalQty;
    var atcPrice = document.querySelector('[data-atc-price]');
    var atcCompare = document.querySelector('[data-atc-compare]');

    if (atcPrice) atcPrice.textContent = bmsmFormatMoney(finalTotal);
    if (atcCompare) {
      if (compareTotal > finalTotal) {
        atcCompare.textContent = bmsmFormatMoney(isSubscribed ? discountedTotal : compareTotal);
        atcCompare.style.display = 'inline';
      } else {
        atcCompare.style.display = 'none';
      }
    }

    /* Also update the page's main price display to show the discounted per-unit price.
       This keeps the visible price consistent with what gets charged. */
    if (priceEl && typeof window.formatMoney === 'function') {
      var perUnitDiscounted = isSubscribed
        ? Math.ceil(unitDiscounted * 0.85)
        : (discountRate > 0 ? unitDiscounted : basePrice);
      priceEl.innerHTML = window.formatMoney(perUnitDiscounted, fmt);
    }
    if (compareEl && breaksEl) {
      if (discountRate > 0 && basePrice > unitDiscounted) {
        if (typeof window.formatMoney === 'function') {
          compareEl.innerHTML = window.formatMoney(basePrice, fmt);
        }
        compareEl.classList.add('sale');
        breaksEl.classList.add('sale');
      }
    }

    /* Force fixed per-unit display on PDP main price regardless of BMSM tier. */
    if (priceEl && typeof window.formatMoney === 'function') {
      priceEl.innerHTML = window.formatMoney(basePrice, fmt);
    }
    if (compareEl && breaksEl) {
      compareEl.classList.remove('sale');
      breaksEl.classList.remove('sale');
    }

    if (typeof window.updateRcTogglePrices === 'function') {
      window.updateRcTogglePrices(discountedTotal);
    }
  }

  function bmsmUpdateOptionPrices() {
    var basePrice = bmsmGetBasePrice();
    if (!basePrice) return;

    bmsmOptionPriceEls.forEach(function (el) {
      var qty = parseInt(el.getAttribute('data-bmsm-option-price'), 10) || 1;
      var rate = bmsmGetDiscountRate(qty);
      var unitDisc = Math.ceil(basePrice * (1 - rate));
      var discounted = unitDisc * qty;
      el.textContent = bmsmFormatMoney(discounted);
    });

    bmsmOptionCompareEls.forEach(function (el) {
      var qty = parseInt(el.getAttribute('data-bmsm-option-compare'), 10) || 1;
      var rate = bmsmGetDiscountRate(qty);
      var compare = basePrice * qty;
      if (rate > 0) {
        el.textContent = bmsmFormatMoney(compare);
        el.style.display = '';
      } else {
        el.textContent = '';
        el.style.display = 'none';
      }
    });
  }

  function bmsmRefreshUi() {
    if (!bmsmPanel) return;

    var total = bmsmTotalSelected();
    var multiplier = bmsmGetQtyMultiplier();
    var totalWithMultiplier = total * multiplier;
    var target = bmsmState.target;
    var discountRate = bmsmGetDiscountRate(totalWithMultiplier);
    var progressPct = Math.max(0, Math.min(100, (totalWithMultiplier / 3) * 100));

    bmsmTargetButtons.forEach(function (btn) {
      var btnTarget = parseInt(btn.getAttribute('data-bmsm-target'), 10) || 1;
      btn.classList.toggle('is-active', btnTarget === target);
    });

    if (bmsmBadge) {
      if (discountRate > 0) {
        bmsmBadge.textContent = Math.round(discountRate * 100) + '% OFF UNLOCKED';
        bmsmBadge.style.setProperty('display', 'inline-block', 'important');
      } else {
        bmsmBadge.style.display = 'none';
      }
    }

    if (bmsmProgressFill) bmsmProgressFill.style.width = progressPct + '%';

    if (bmsmSubtitle) {
      bmsmSubtitle.textContent = totalWithMultiplier === 1 ? '1 bag' : (totalWithMultiplier + ' bags');
    }

    if (bmsmMessage) {
      if (totalWithMultiplier >= 3) bmsmMessage.innerHTML = '<strong>Max discount unlocked!</strong>';
      else if (totalWithMultiplier === 2) bmsmMessage.innerHTML = 'Add <strong>one</strong> more bag to unlock ' + bmsmDiscount2 + '% off';
      else bmsmMessage.innerHTML = 'Add <strong>one</strong> more bag to unlock ' + bmsmDiscount1 + '% off';
    }

    if (bmsmHideBtn) {
      bmsmHideBtn.setAttribute('aria-expanded', bmsmState.expanded ? 'true' : 'false');
    }

    bmsmRows.forEach(function (row) {
      var variantId = String(row.dataset.activeVariantId || row.dataset.variantId || '');
      var qty = parseInt(bmsmState.quantities[variantId], 10) || 0;
      var qtyValue = row.querySelector('.pdp-ss__bmsm-qty-value');
      var decBtn = row.querySelector('.pdp-ss__bmsm-qty-btn[data-action="dec"]');
      var incBtn = row.querySelector('.pdp-ss__bmsm-qty-btn[data-action="inc"]');
      var qtyWrap = row.querySelector('.pdp-ss__bmsm-qty');
      var addBtn = row.querySelector('.pdp-ss__bmsm-qty-add');
      var isAvailable = row.dataset.activeAvailable !== 'false';

      if (qtyValue) qtyValue.textContent = String(qty);
      var isCurrentRow = row.dataset.current === 'true';
      var minRowQty = isCurrentRow ? 1 : 0;
      if (decBtn) decBtn.disabled = !isAvailable || qty <= minRowQty;
      if (incBtn) incBtn.disabled = !isAvailable;
      if (addBtn) addBtn.disabled = !isAvailable;
      if (qtyWrap) qtyWrap.dataset.qtyZero = (qty === 0) ? 'true' : 'false';
      row.classList.toggle('pdp-ss__bmsm-row--selected', qty > 0);
    });

    /* Determine whether the mix summary card should be visible.
       Only show when the user has a custom mix — i.e. multiple flavors selected,
       or a quantity that doesn't match 1/2/3 of the preferred (current) product.
       If it's simply 1, 2, or 3 of the current product, go back to the option buttons. */
    var bmsmShowMixSummary = (function () {
      if (bmsmState.expanded || !bmsmState.userCollapsed) return false;
      var totalQty = bmsmTotalSelected();
      if (totalQty <= 0) return false;
      var fanRows = bmsmRows.filter(function (row) {
        var vid = String(row.dataset.activeVariantId || row.dataset.variantId || '');
        return (parseInt(bmsmState.quantities[vid], 10) || 0) > 0;
      });
      if (fanRows.length === 0) return false;
      /* If only one flavor is selected and its qty is 1, 2, or 3, treat as a standard target */
      if (fanRows.length === 1 && totalQty >= 1 && totalQty <= 3) {
        /* Snap the target back to match and skip summary */
        bmsmState.target = totalQty;
        return false;
      }
      return true;
    })();

    if (bmsmToggle) bmsmToggle.setAttribute('aria-expanded', bmsmState.expanded ? 'true' : 'false');
    if (bmsmToggle) bmsmToggle.textContent = '+ MIX & MATCH FLAVORS';
    /* Hide toggle when expanded (list visible) or when mix summary is showing */
    if (bmsmToggle) bmsmToggle.style.display = (bmsmState.expanded || bmsmShowMixSummary) ? 'none' : '';
    /* Hide the 1/2/3-bag option buttons when the mix summary card is visible */
    if (bmsmOptions) bmsmOptions.style.display = (bmsmState.expanded || bmsmShowMixSummary) ? 'none' : '';
    /* Show actions when list is expanded OR when mix summary is showing (with Show label in the latter case) */
    if (bmsmActions) bmsmActions.style.display = (bmsmState.expanded || bmsmShowMixSummary) ? 'flex' : 'none';
    if (bmsmHideBtn) {
      var hideLabel = bmsmHideBtn.querySelector('.pdp-ss__bmsm-action__show-label');
      var labelText = bmsmShowMixSummary ? 'Show' : 'Hide';
      if (hideLabel) hideLabel.textContent = labelText;
      else bmsmHideBtn.textContent = labelText;
      bmsmHideBtn.classList.toggle('is-expanded', !bmsmShowMixSummary && bmsmState.expanded);
    }
    if (bmsmList) bmsmList.style.display = bmsmState.expanded ? '' : 'none';

    /* Mix summary card: show when collapsed and multiple distinct flavors or qty > 1 */
    (function bmsmRefreshMixSummary() {
      if (!bmsmMixSummary || !bmsmMixFan) return;

      var basePrice = bmsmGetBasePrice();
      var totalQty = bmsmTotalSelected();
      var multiplier = bmsmGetQtyMultiplier();
      var totalBags = totalQty * multiplier;
      var discountRate = bmsmGetDiscountRate(totalBags);
      var unitDisc = Math.ceil(basePrice * (1 - discountRate));
      var totalPrice = unitDisc * totalBags;

      if (!bmsmShowMixSummary) {
        bmsmMixSummary.style.display = 'none';
        return;
      }

      /* Build fan images — one img per bag for every selected flavor (no cap) */
      bmsmMixFan.innerHTML = '';
      var fanImages = [];
      var selectedRows = bmsmRows.filter(function (row) {
        var vid = String(row.dataset.activeVariantId || row.dataset.variantId || '');
        return (parseInt(bmsmState.quantities[vid], 10) || 0) > 0;
      });
      selectedRows.forEach(function (row) {
        var vid = String(row.dataset.activeVariantId || row.dataset.variantId || '');
        var qty = parseInt(bmsmState.quantities[vid], 10) || 0;
        var src = row.dataset.featuredImage || '';
        if (!src) {
          var imgEl = row.querySelector('img.pdp-ss__bmsm-icon, img');
          src = imgEl ? imgEl.src : '';
        }
        if (!src) return;
        for (var i = 0; i < qty; i++) fanImages.push(src);
      });
      fanImages.forEach(function (src) {
        var el = document.createElement('img');
        el.src = src;
        el.alt = '';
        el.width = 80;
        el.height = 115;
        bmsmMixFan.appendChild(el);
      });

      /* Label: "YOUR MIX · N BAGS" */
      if (bmsmMixLabel) {
        bmsmMixLabel.textContent = 'YOUR MIX \u00B7 ' + totalBags + (totalBags === 1 ? ' BAG' : ' BAGS');
      }

      /* Price — show discounted total with strikethrough compare-at when discount is active */
      if (bmsmMixPrice) {
        var compareTotal = basePrice * totalBags;
        if (discountRate > 0 && compareTotal > totalPrice) {
          bmsmMixPrice.innerHTML = '<span class="pdp-ss__bmsm-mix-compare">' + bmsmFormatMoney(compareTotal) + '</span> ' + bmsmFormatMoney(totalPrice);
        } else {
          bmsmMixPrice.textContent = bmsmFormatMoney(totalPrice);
        }
      }

      if (bmsmMixDiscount) {
        var discountPct = Math.round(discountRate * 100);
        if (discountPct > 0) {
          bmsmMixDiscount.textContent = discountPct + '% Off';
          bmsmMixDiscount.style.display = '';
        } else {
          bmsmMixDiscount.style.display = 'none';
        }
      }

      bmsmMixSummary.style.display = '';
    })();

    bmsmUpdateOptionPrices();
    bmsmSyncFormInputs();
    bmsmUpdateAtcPrice();
  }

  function bmsmSetTarget(target) {
    bmsmState.target = target;
    bmsmNormalizeToTarget();
    bmsmRefreshUi();
  }

  function bmsmNormalizePlanName(name) {
    return String(name || '')
      .replace(/:\s*save\s+\d+%/i, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function bmsmGetSubscriptionContext() {
    var rcToggleBtn = document.getElementById('rc-toggle-btn');
    var isSubscribed = !!(rcToggleBtn && rcToggleBtn.getAttribute('aria-expanded') === 'true');
    var sellingPlanInput = document.getElementById('selling-plan-input');
    var selectedId = sellingPlanInput && sellingPlanInput.value ? String(sellingPlanInput.value) : '';
    var freqSelect = document.getElementById('rc-freq-select');
    var selectedName = '';

    if (freqSelect && freqSelect.selectedIndex >= 0 && freqSelect.options[freqSelect.selectedIndex]) {
      selectedName = freqSelect.options[freqSelect.selectedIndex].textContent || '';
    }

    return {
      enabled: isSubscribed,
      selectedId: selectedId,
      selectedNameKey: bmsmNormalizePlanName(selectedName)
    };
  }

  function bmsmResolveRowSellingPlanId(row, variantId, subCtx) {
    if (!row || !variantId || !subCtx || !subCtx.enabled) return '';
    var entries = row._bmsmVariantMap || [];
    if (!entries.length) return '';

    var entry = entries.find(function (item) {
      return String(item && item.variant_id || '') === String(variantId);
    });
    if (!entry) return '';

    var plans = Array.isArray(entry.selling_plans) ? entry.selling_plans : [];
    if (!plans.length) return '';

    if (subCtx.selectedId) {
      var byId = plans.find(function (plan) {
        return String(plan && plan.id || '') === subCtx.selectedId;
      });
      if (byId && byId.id) return String(byId.id);
    }

    if (subCtx.selectedNameKey) {
      var byName = plans.find(function (plan) {
        return bmsmNormalizePlanName(plan && plan.name) === subCtx.selectedNameKey;
      });
      if (byName && byName.id) return String(byName.id);
    }

    return plans[0] && plans[0].id ? String(plans[0].id) : '';
  }

  function bmsmApplyRowDelta(variantId, delta) {
    if (!variantId || !delta) return;
    var currentQty = parseInt(bmsmState.quantities[variantId], 10) || 0;
    var nextQty = currentQty + delta;
    /* Current flavor row stays at min 1; other rows can drop to 0 */
    var isCurrentFlavor = bmsmRows.some(function (r) {
      return r.dataset.current === 'true' &&
        String(r.dataset.activeVariantId || r.dataset.variantId || '') === variantId;
    });
    var minQty = isCurrentFlavor ? 1 : 0;
    if (nextQty < minQty) nextQty = minQty;

    bmsmState.quantities[variantId] = nextQty;

    /* Advance discount target when total exceeds current target;
       retreat it when total drops below target so discount tier stays accurate. */
    var newTotal = bmsmTotalSelected();
    if (newTotal > bmsmState.target) {
      bmsmState.target = Math.min(3, newTotal);
    } else if (newTotal < bmsmState.target) {
      bmsmState.target = Math.max(1, Math.min(3, newTotal));
    }

    bmsmRefreshUi();
    window.requestAnimationFrame(function () { anchorToSelectASize(); });
  }

  function bmsmApplyRowDeltaForRow(row, delta) {
    if (!row) return;
    var variantId = String(row.dataset.activeVariantId || row.dataset.variantId || '');
    bmsmApplyRowDelta(variantId, delta);
  }

  function bmsmResetListConstraint() {
    if (!bmsmList) return;
    bmsmList.style.maxHeight = '';
    bmsmList.style.overflowY = '';
  }

  function bmsmConstrainListOnMobile() {
    if (!bmsmList || !bmsmPanel) return;
    if (!window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return;

    function _apply() {
      var atcWrap = document.querySelector('.pdp-atc-wrap');
      var atcHeight = atcWrap ? atcWrap.offsetHeight + 8 : 0;
      var listRect = bmsmList.getBoundingClientRect();
      var available = window.innerHeight - listRect.top - atcHeight - 16;
      if (available > 80) {
        bmsmList.style.maxHeight = available + 'px';
        bmsmList.style.overflowY = 'auto';
      }
    }

    /* Delay slightly so sticky ATC has time to unstick first */
    window.setTimeout(function () {
      window.requestAnimationFrame(function () {
        _apply();
        window.requestAnimationFrame(_apply);
      });
    }, 60);
    window.setTimeout(_apply, 200);
  }

  function bmsmInitInteractions() {
    if (!bmsmPanel) return;

    bmsmTargetButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var nextTarget = parseInt(btn.getAttribute('data-bmsm-target'), 10) || 1;
        bmsmState.userCollapsed = false;
        bmsmSetTarget(nextTarget);
      });
    });

    if (bmsmToggle) {
      bmsmToggle.addEventListener('click', function () {
        bmsmState.expanded = true;
        bmsmRefreshUi();
        bmsmConstrainListOnMobile();
        /* Trigger sticky ATC recalculation on mobile */
        document.dispatchEvent(new Event('pdp:layout-changed'));
      });
    }

    if (bmsmHideBtn) {
      bmsmHideBtn.addEventListener('click', function () {
        /* If the mix summary is currently showing, "Show" expands the list;
           if the list is expanded, "Hide" collapses back to summary/collapsed state. */
        var totalQty = bmsmTotalSelected();
        var hasMixSummary = !bmsmState.expanded && bmsmState.userCollapsed && totalQty > 1;
        if (hasMixSummary) {
          bmsmState.expanded = true;
          bmsmState.userCollapsed = false;
          bmsmRefreshUi();
          bmsmConstrainListOnMobile();
        } else {
          bmsmState.expanded = false;
          bmsmState.userCollapsed = true;
          bmsmResetListConstraint();
          bmsmRefreshUi();
        }
        document.dispatchEvent(new Event('pdp:layout-changed'));
      });
    }

    if (bmsmClearBtn) {
      bmsmClearBtn.addEventListener('click', function () {
        var activeVariantId = bmsmPreferredRowVariantId();
        bmsmState.target = 1;
        bmsmState.expanded = false;
        bmsmState.userCollapsed = false;
        bmsmSetBaseQuantities(activeVariantId);
        bmsmNormalizeToTarget();
        bmsmRefreshUi();
        document.dispatchEvent(new Event('pdp:layout-changed'));
      });
    }

    bmsmRows.forEach(function (row) {
      var decBtn = row.querySelector('.pdp-ss__bmsm-qty-btn[data-action="dec"]');
      var incBtn = row.querySelector('.pdp-ss__bmsm-qty-btn[data-action="inc"]');
      var addBtn = row.querySelector('.pdp-ss__bmsm-qty-add[data-action="add"]');
      if (decBtn) decBtn.addEventListener('click', function (e) { e.stopPropagation(); bmsmApplyRowDeltaForRow(row, -1); });
      if (incBtn) incBtn.addEventListener('click', function (e) { e.stopPropagation(); bmsmApplyRowDeltaForRow(row, 1); });
      if (addBtn) addBtn.addEventListener('click', function (e) { e.stopPropagation(); bmsmApplyRowDeltaForRow(row, 1); });

      /* Tap on row body adds */
      row.addEventListener('click', function (e) {
        if (e.target.closest('.pdp-ss__bmsm-qty')) return;
        bmsmApplyRowDeltaForRow(row, 1);
      });

      /* Swipe right to add, swipe left to subtract */
      var SWIPE_THRESHOLD = 30;
      row.addEventListener('touchstart', function (e) {
        var touch = e.touches[0];
        var startX = touch.clientX;
        var startY = touch.clientY;
        var fired = false;
        var dirDetected = false;
        var isHorizontal = false;
        var el = row;

        function onMove(ev) {
          if (fired) return;
          var t = ev.touches[0];
          var dx = t.clientX - startX;
          var dy = t.clientY - startY;

          if (!dirDetected) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            if (Math.abs(dy) > Math.abs(dx)) {
              el.removeEventListener('touchmove', onMove);
              return;
            }
            dirDetected = true;
            isHorizontal = true;
          }

          if (isHorizontal) ev.preventDefault();

          if (dx > SWIPE_THRESHOLD) {
            fired = true;
            bmsmApplyRowDeltaForRow(row, 1);
          } else if (dx < -SWIPE_THRESHOLD) {
            fired = true;
            bmsmApplyRowDeltaForRow(row, -1);
          }
        }

        function cleanup() {
          el.removeEventListener('touchmove', onMove);
          el.removeEventListener('touchend', cleanup);
          el.removeEventListener('touchcancel', cleanup);
        }

        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', cleanup);
        el.addEventListener('touchcancel', cleanup);
      });
    });

    window.addEventListener('resize', function () {
      if (!bmsmState.expanded) bmsmResetListConstraint();
    }, { passive: true });

    /* When the qty picker changes (user clicks +/-), recompute the ATC price
       since it now acts as a multiplier on the BMSM bundle.
       The qty handlers in product-form.liquid and section-product-info.js
       dispatch a 'change' event on the input after updating the value. */
    var qtyInputForBmsm = document.querySelector('.ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"]');
    if (qtyInputForBmsm) {
      qtyInputForBmsm.addEventListener('change', function () {
        if (bmsmState.active) bmsmRefreshUi();
      });
    }
  }

  function bmsmRefreshVisibility() {
    if (!isBmsmProduct || !bmsmPanel) return;

    var activeOpt = getActiveSizeOption();
    var isByo = !!(activeOpt && activeOpt.dataset.isByo === 'true');
    var isTagged = !!(activeOpt && activeOpt.dataset.isBmsmTagged === 'true');
    bmsmSyncRowsForTenServingBags();
    var bmsmVariantId = bmsmPreferredRowVariantId();
    var hasEligibleTenServingBag = !!(bmsmPanel.dataset.bmsmUnitVariantId || '');
    var shouldShow = !!activeOpt && !isByo && isTagged && isOptionAvailable(activeOpt) && hasEligibleTenServingBag && !!bmsmVariantId;

    bmsmState.active = shouldShow;
    bmsmPanel.style.display = shouldShow ? '' : 'none';
    document.dispatchEvent(new CustomEvent('pdp:layout-changed'));

    if (!shouldShow) {
      /* Reset qty to 1 when switching away from BMSM */
      var qtyInput = document.querySelector('.ae--form-quantity input[name="quantity"]');
      if (qtyInput) qtyInput.value = '1';
      return;
    }

    var activeVariantId = bmsmVariantId;
    if (!bmsmState.activeVariantId || bmsmState.activeVariantId !== activeVariantId) {
      bmsmState.activeVariantId = activeVariantId;
      bmsmState.target = 1;
      bmsmState.expanded = false;
      bmsmState.userCollapsed = false;
      bmsmResetListConstraint();
      bmsmSetBaseQuantities(activeVariantId || bmsmPreferredRowVariantId());
      bmsmNormalizeToTarget();
    }

    bmsmRefreshUi();
    bmsmConstrainListOnMobile();
  }

  function bmsmCollectQuantitiesFromDom() {
    var quantities = {};
    bmsmRows.forEach(function (row) {
      if (row.style.display === 'none') return;
      var variantId = String(row.dataset.activeVariantId || row.dataset.variantId || '');
      if (!variantId) return;
      var qtyEl = row.querySelector('.pdp-ss__bmsm-qty-value');
      var qty = qtyEl ? (parseInt(qtyEl.textContent, 10) || 0) : 0;
      if (qty > 0) quantities[variantId] = qty;
    });
    return quantities;
  }

  function bmsmBuildAddItems(quantitiesOverride) {
    var subCtx = bmsmGetSubscriptionContext();
    var sellingPlanInput = document.getElementById('selling-plan-input');
    var quantities = quantitiesOverride && Object.keys(quantitiesOverride).length
      ? quantitiesOverride
      : bmsmState.quantities;

    var qtyMultiplier = bmsmGetQtyMultiplier();
    return Object.keys(quantities).reduce(function (items, variantId) {
      var qty = parseInt(quantities[variantId], 10) || 0;
      if (qty <= 0) return items;

      var item = {
        id: parseInt(variantId, 10),
        quantity: qty * qtyMultiplier
      };

      var row = bmsmRows.find(function (rowEl) {
        var activeId = String(rowEl.dataset.activeVariantId || rowEl.dataset.variantId || '');
        return activeId === String(variantId);
      });
      var resolvedPlanId = bmsmResolveRowSellingPlanId(row, variantId, subCtx);
      if (!resolvedPlanId && subCtx.enabled && sellingPlanInput && sellingPlanInput.value) {
        resolvedPlanId = String(sellingPlanInput.value);
      }

      if (resolvedPlanId) {
        item.selling_plan = resolvedPlanId;
      }

      item.properties = {
        _bmsm_bundle_size: String(bmsmState.target)
      };

      items.push(item);
      return items;
    }, []);
  }

  function bmsmSubmitNativeForm(form) {
    if (!form) return;
    try {
      bmsmBypassInterceptOnce = true;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    } catch (e) {
      bmsmBypassInterceptOnce = false;
    }
  }

  function bmsmStripSellingPlan(items) {
    return (items || []).map(function (item) {
      var cloned = {
        id: item.id,
        quantity: item.quantity,
        properties: item.properties
      };
      return cloned;
    });
  }

  /* ── Page elements to update on variant change ── */
  var priceEl = document.querySelector('.current-price-pdp');
  var compareEl = document.querySelector('.compare-at-price-pdp');
  var breaksEl = document.querySelector('.breaks');
  var variantSelect = document.querySelector('#product-variant-id');
  var fmt = window.moneyFormat || '${{amount}}';

  /* ── Media data for gallery swap (pre-rendered hidden sets) ── */
  var currentGalleryProductId = null;

  /* Cache the original .ae--product-media innerHTML so we can restore it
     when switching back to the page's own product. */
  var originalMediaContainer = document.querySelector('.ae--product-media');
  var originalMediaHtml = originalMediaContainer ? originalMediaContainer.innerHTML : '';
  var originalIngredientsImage = document.querySelector('.ae--product-ingredients-section-image');
  var originalIngredientsImageSrc = originalIngredientsImage ? originalIngredientsImage.getAttribute('src') : '';
  var originalSupplementModalContent = document.querySelector('.ae--modal .ae--modal-content');
  var originalSupplementModalHtml = originalSupplementModalContent ? originalSupplementModalContent.innerHTML : '';
  var originalAccordionFacts = Array.from(document.querySelectorAll('.cnr-product-accordion-ingredients .ae--product-accordion-content')).map(function (content) {
    var factsRoot = content.querySelector('.supplements-slider') || content.querySelector('.supplement-facts--table');
    return {
      content: content,
      html: factsRoot ? factsRoot.outerHTML : ''
    };
  });
  var currentIngredientsProductId = null;
  var originalProductId = container.dataset.currentVariantId
    ? (function () {
        /* Derive the page product's Shopify product ID from the first
           size option whose variant matches the page variant. */
        var pageVid = container.dataset.currentVariantId;
        var match = container.querySelector('[data-variant-id="' + pageVid + '"]');
        return match ? match.dataset.productId : null;
      })()
    : null;

  function initIngredientsSliders() {
    if (typeof Splide === 'undefined') return;
    var options = {
      perPage: 1,
      perMove: 1,
      pagination: true,
      arrows: true,
      rewind: true,
      speed: 800
    };

    var desktop = document.querySelector('#ingredients-slider-desktop');
    if (desktop) {
      new Splide('#ingredients-slider-desktop', options).mount();
    }

    var mobile = document.querySelector('#ingredients-slider-mobile');
    if (mobile) {
      new Splide('#ingredients-slider-mobile', options).mount();
    }
  }

  function replaceAccordionSupplementFacts(html) {
    var accordionContents = document.querySelectorAll('.cnr-product-accordion-ingredients .ae--product-accordion-content');
    if (!accordionContents.length) return;

    accordionContents.forEach(function (content) {
      var existingSlider = content.querySelector('.supplements-slider');
      if (existingSlider) existingSlider.remove();
      var existingFacts = content.querySelector('.supplement-facts--table');
      if (existingFacts) existingFacts.remove();

      if (!html) return;

      var iconList = content.querySelector('.ae--product-accordion-icon-list');
      if (iconList) iconList.insertAdjacentHTML('afterend', html);
      else content.insertAdjacentHTML('beforeend', html);

      var insertedSlider = content.querySelector('.pdp-ss__supplements-slider-template, .supplements-slider');
      if (insertedSlider) {
        insertedSlider.classList.add('supplements-slider');
        insertedSlider.classList.remove('pdp-ss__supplements-slider-template');
        if (!insertedSlider.id) insertedSlider.id = 'ingredients-slider-mobile';
      }
    });
  }

  function restoreOriginalAccordionSupplementFacts() {
    originalAccordionFacts.forEach(function (entry) {
      if (!entry || !entry.content) return;
      var existingSlider = entry.content.querySelector('.supplements-slider');
      if (existingSlider) existingSlider.remove();
      var existingFacts = entry.content.querySelector('.supplement-facts--table');
      if (existingFacts) existingFacts.remove();
      if (entry.html) {
        var iconList = entry.content.querySelector('.ae--product-accordion-icon-list');
        if (iconList) iconList.insertAdjacentHTML('afterend', entry.html);
        else entry.content.insertAdjacentHTML('beforeend', entry.html);
      }
    });
  }

  function getOptionServingCount(opt) {
    if (!opt) return 0;
    var source = [
      opt.dataset.option1 || '',
      opt.dataset.option2 || '',
      opt.dataset.option3 || '',
      opt.dataset.packageName || ''
    ].join(' ');
    var match = source.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  function findSameCountVariantSet(selector, variantDataAttribute, servingCount) {
    if (!servingCount) return null;
    return Array.from(document.querySelectorAll(selector)).find(function (set) {
      var setVariantId = set.getAttribute(variantDataAttribute);
      if (!setVariantId) return false;
      var setOption = container.querySelector('[data-variant-id="' + setVariantId + '"]');
      return getOptionServingCount(setOption) === servingCount;
    }) || null;
  }

  function updateIngredientsSection(productId, variantId, servingCount, featuredImageUrl) {
    if (!productId) return;
    productId = String(productId);
    variantId = variantId ? String(variantId) : '';
    var cacheKey = variantId || productId;
    if (currentIngredientsProductId === cacheKey) return;
    currentIngredientsProductId = cacheKey;

    var ingredientsImageEl = document.querySelector('.ae--product-ingredients-section-image');
    var supplementModalContent = document.querySelector('.ae--modal .ae--modal-content');
    /* Prefer the variant-scoped set (always unique); fall back to product-scoped
       (used by BYO, which intentionally points at a different product's media). */
    var ingredientsImageSet = variantId
      ? document.querySelector('.pdp-ss__ingredients-image-set[data-ingredients-variant-id="' + variantId + '"]')
      : null;
    var ingredientsSetMatchesVariant = !!ingredientsImageSet;
    if (!ingredientsImageSet && servingCount) {
      ingredientsImageSet = findSameCountVariantSet(
        '.pdp-ss__ingredients-image-set[data-ingredients-variant-id]',
        'data-ingredients-variant-id',
        servingCount
      );
    }
    if (!ingredientsImageSet && !featuredImageUrl) {
      ingredientsImageSet = document.querySelector('.pdp-ss__ingredients-image-set[data-ingredients-product-id="' + productId + '"]');
    }
    var supplementSet = variantId
      ? document.querySelector('.pdp-ss__supplement-set[data-supplement-variant-id="' + variantId + '"]')
      : null;
    if (!supplementSet && servingCount) {
      supplementSet = findSameCountVariantSet(
        '.pdp-ss__supplement-set[data-supplement-variant-id]',
        'data-supplement-variant-id',
        servingCount
      );
    }
    if (!supplementSet && (!variantId || productId !== originalProductId)) {
      supplementSet = document.querySelector('.pdp-ss__supplement-set[data-supplement-product-id="' + productId + '"]');
    }

    if (ingredientsImageEl) {
      var nextSrc = ingredientsSetMatchesVariant && ingredientsImageSet
        ? ingredientsImageSet.getAttribute('data-ingredients-image-src')
        : featuredImageUrl || (ingredientsImageSet ? ingredientsImageSet.getAttribute('data-ingredients-image-src') : '');
      if (!nextSrc && productId === originalProductId) nextSrc = originalIngredientsImageSrc;
      if (nextSrc) {
        ingredientsImageEl.setAttribute('src', nextSrc);
        ingredientsImageEl.style.display = '';
      } else {
        ingredientsImageEl.removeAttribute('src');
        ingredientsImageEl.style.display = 'none';
      }
    }

    if (supplementModalContent) {
      if (supplementSet && supplementSet.innerHTML.trim()) {
        supplementModalContent.innerHTML = supplementSet.innerHTML;
        var sliderTemplate = supplementModalContent.querySelector('.pdp-ss__supplements-slider-template');
        if (sliderTemplate && !sliderTemplate.id) sliderTemplate.id = 'ingredients-slider-desktop';
      } else if (productId === originalProductId && originalSupplementModalHtml) {
        supplementModalContent.innerHTML = originalSupplementModalHtml;
      } else {
        supplementModalContent.innerHTML = '';
      }
    }

    if (supplementSet && supplementSet.innerHTML.trim()) {
      replaceAccordionSupplementFacts(supplementSet.innerHTML);
    } else if (productId === originalProductId) {
      restoreOriginalAccordionSupplementFacts();
    } else {
      replaceAccordionSupplementFacts('');
    }

    initIngredientsSliders();
  }

  function updateGallery(productId, force, variantId, featuredImageUrl) {
    if (!productId) return;
    productId = String(productId);
    variantId = variantId ? String(variantId) : '';
    var cacheKey = variantId || productId;
    if (!force && currentGalleryProductId === cacheKey) return;
    currentGalleryProductId = cacheKey;

    var mediaContainer = document.querySelector('.ae--product-media');
    if (!mediaContainer) return;

    /* Remove BYO fan artifacts */
    mediaContainer.classList.remove('byo-fan-active');
    var fanEl = mediaContainer.querySelector('.byo-fan');
    if (fanEl) fanEl.remove();

    /* Prefer the variant-scoped set (always unique per size); fall back to
       product-scoped (used by BYO, which intentionally points at a different
       product's media). */
    var mediaSet = variantId
      ? document.querySelector('.pdp-ss__media-set[data-media-variant-id="' + variantId + '"]')
      : null;
    if (!mediaSet && (!variantId || !featuredImageUrl || productId !== originalProductId)) {
      mediaSet = document.querySelector('.pdp-ss__media-set[data-media-product-id="' + productId + '"]');
    }

    if (mediaSet) {
      /* Clone the hidden set's children into the live media container */
      mediaContainer.innerHTML = mediaSet.innerHTML;
    } else if (featuredImageUrl) {
      /* Variant blocks can be absent when Shopify option drops fail to resolve.
         Restore the base gallery and explicitly replace its hero image. */
      if (productId === originalProductId) mediaContainer.innerHTML = originalMediaHtml;
      var featuredImage = mediaContainer.querySelector('.ae--product-featured-image img');
      if (featuredImage) {
        featuredImage.src = featuredImageUrl;
        featuredImage.removeAttribute('srcset');
      }
    } else if (productId === originalProductId) {
      /* Restore the page product's original gallery */
      mediaContainer.innerHTML = originalMediaHtml;
    }

    /* Re-bind PDP modal click listeners if the function exists */
    if (typeof initPdpModalListeners === 'function') {
      initPdpModalListeners();
    }

    /* Keep the zoom modal slides/order in sync with the swapped-in gallery */
    if (typeof window.syncPdpModalMedia === 'function') {
      window.syncPdpModalMedia();
    }

    /* Reset scroll position */
    var scrollContent = document.getElementById('scrollContent');
    if (scrollContent) scrollContent.scrollLeft = 0;
  }
  if (variantSelect) {
    if (initActiveOpt && initActiveOpt.dataset.variantId) {
      if (!variantSelect.querySelector('option[value="' + initActiveOpt.dataset.variantId + '"]')) {
        var initialVariantOption = document.createElement('option');
        initialVariantOption.value = initActiveOpt.dataset.variantId;
        initialVariantOption.dataset.price = initActiveOpt.dataset.price || '';
        initialVariantOption.dataset.compareAtPrice = initActiveOpt.dataset.compareAtPrice || '0';
        initialVariantOption.textContent = initActiveOpt.dataset.packageName || '';
        variantSelect.appendChild(initialVariantOption);
      }
      variantSelect.value = initActiveOpt.dataset.variantId;
    }
    var selectedOpt = variantSelect.options[variantSelect.selectedIndex];
    if (selectedOpt) {
      var initPrice = parseInt(selectedOpt.dataset.price, 10) || 0;
      var initCompareAt = parseInt(selectedOpt.dataset.compareAtPrice, 10) || 0;
      window._pdpCurrentVariant = { price: initPrice, compareAt: initCompareAt };
    }
  }

  /* ── Format Split Mode ── */
  if (mode === 'format_split') {
    var formatBtns = container.querySelectorAll('.pdp-ss__format-btn');
    var sizeOptions = container.querySelectorAll('.pdp-ss__size-option');
    sizeList = container.querySelector('.pdp-ss__size-list--dropdown');
    var sizeLegend = sizeList
      ? sizeList.closest('.pdp-ss__fieldset').querySelector('.pdp-ss__label.ae--product-variant-label')
      : null;

    if (sizeList && sizeOptions && sizeOptions.length) {
      var splitOptions = Array.from(sizeOptions);
      var splitOriginalIndex = new Map();
      splitOptions.forEach(function (opt, idx) { splitOriginalIndex.set(opt, idx); });

      var formatOrder = [];
      splitOptions.forEach(function (opt) {
        var fmtKey = opt.dataset.format || '';
        if (formatOrder.indexOf(fmtKey) === -1) formatOrder.push(fmtKey);
      });

      function splitServingsCount(opt) {
        var source = [
          opt.dataset.option1 || '',
          opt.dataset.packageName || '',
          opt.querySelector('.pdp-ss__size-name') ? opt.querySelector('.pdp-ss__size-name').textContent : ''
        ].join(' ').toLowerCase();
        var m = source.match(/(\d+)\s*serv/);
        return m ? parseInt(m[1], 10) : 0;
      }

      function splitIsBag(opt) {
        var source = [
          opt.dataset.packageName || '',
          opt.dataset.option1 || '',
          opt.querySelector('.pdp-ss__size-name') ? opt.querySelector('.pdp-ss__size-name').textContent : ''
        ].join(' ').toLowerCase();
        return source.indexOf('bag') !== -1;
      }

      splitOptions.sort(function (a, b) {
        var aFmt = a.dataset.format || '';
        var bFmt = b.dataset.format || '';
        var aFmtIdx = formatOrder.indexOf(aFmt);
        var bFmtIdx = formatOrder.indexOf(bFmt);
        if (aFmtIdx !== bFmtIdx) return aFmtIdx - bFmtIdx;

        /* Keep BYO at the end within a format group. */
        var aByo = a.dataset.isByo === 'true';
        var bByo = b.dataset.isByo === 'true';
        if (aByo !== bByo) return aByo ? 1 : -1;

        /* Within bags, largest serving count first. */
        var aBag = splitIsBag(a);
        var bBag = splitIsBag(b);
        if (aBag && bBag) {
          var aServ = splitServingsCount(a);
          var bServ = splitServingsCount(b);
          if (aServ !== bServ) return bServ - aServ;
        }

        return (splitOriginalIndex.get(a) || 0) - (splitOriginalIndex.get(b) || 0);
      });

      splitOptions.forEach(function (opt) { sizeList.appendChild(opt); });
    }

    function syncPackExpanderState() {
      if (!sizeList) return;
      var isSingle = getVisiblePackOptionCount() <= 1;
      sizeList.classList.toggle('pdp-ss__size-list--single', isSingle);
      if (isSingle) {
        /* Keep the list open when the sole option is not yet selected,
           so the radio button stays visible for the customer to click. */
        var singleOpt = Array.from(container.querySelectorAll('.pdp-ss__size-option')).find(function (o) {
          return o.style.display !== 'none';
        });
        var singleIsSelected = singleOpt && singleOpt.classList.contains('pdp-ss__size-option--active');
        if (!singleIsSelected) {
          sizeList.classList.add('pdp-ss__size-list--expanded');
        } else {
          sizeList.classList.remove('pdp-ss__size-list--expanded');
        }
      }
      if (sizeLegend) sizeLegend.textContent = isSingle ? 'Size' : 'Select a Size';
    }

    formatBtns.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        if (btn.disabled) return;
        var format = btn.dataset.format;

        /* If this format is already active, just toggle (collapse) the size dropdown. */
        if (btn.classList.contains('pdp-ss__format-btn--active')) {
          if (sizeList) sizeList.classList.toggle('pdp-ss__size-list--expanded');
          return;
        }

        /* On direct user click, anchor to Select a Flavor with a little breathing room. */
        if (event && event.isTrusted) {
          var flavorsEl = document.querySelector('.ae--product-flavors');
          if (flavorsEl) {
            anchorToFieldsetTop(flavorsEl, 16);
          } else {
            var formatFieldset = btn.closest('.pdp-ss__fieldset');
            if (formatFieldset) anchorToFieldsetTop(formatFieldset, 12);
          }
        }

        /* Update active format button */
        formatBtns.forEach(function (b) { b.classList.remove('pdp-ss__format-btn--active'); });
        btn.classList.add('pdp-ss__format-btn--active');

        /* Show/hide size options based on format */
        sizeOptions.forEach(function (opt) {
          if (opt.dataset.format === format) {
            opt.style.display = '';
          } else {
            opt.style.display = 'none';
          }
        });

        syncPackExpanderState();

        /* Hide BYO inline when switching format tabs */
        hideAllByo();

        /* If the active size belongs to this format, keep it selected and
           leave the dropdown collapsed. Otherwise clear selection and expand. */
        var activeInFormat = container.querySelector(
          '.pdp-ss__size-option--active[data-format="' + format + '"]'
        );

        /* If the active option in this format is BYO, restore the inline builder
           and anchor to the top of the flavors section. */
        if (activeInFormat && activeInFormat.dataset.isByo === 'true') {
          showByo(activeInFormat);
          if (event && event.isTrusted) {
            var flavorsAnchor = document.querySelector('.ae--product-flavors');
            if (flavorsAnchor) anchorToFieldsetTop(flavorsAnchor, 16);
          }
        }

        /* Clear any previous preview marker */
        container.querySelectorAll('.pdp-ss__size-option--preview').forEach(function (el) {
          el.classList.remove('pdp-ss__size-option--preview');
        });

        /* If no active item in this format, mark the first visible item as
           preview so it shows in the collapsed state without being selected. */
        if (!activeInFormat) {
          var firstInFormat = container.querySelector(
            '.pdp-ss__size-option[data-format="' + format + '"]:not([style*="display: none"])'
          );
          if (firstInFormat) firstInFormat.classList.add('pdp-ss__size-option--preview');
        }

        /* Always expand when switching to a new format so the user
           can see the available sizes — UNLESS the selected size is
           already in this format, in which case keep it collapsed.
           Do NOT auto-select — only explicit clicks should change the size. */
        if (sizeList) {
          announceCloseAllDropdowns('size-list');
          closePdpDropdowns('size-list');
          if (activeInFormat) {
            /* Selected size is in this format — collapse the dropdown */
            sizeList.classList.remove('pdp-ss__size-list--expanded');
          } else {
            announceDropdownOpened('size-list');
            sizeList.classList.add('pdp-ss__size-list--expanded');
            anchorToFlavorDropdown();
          }
        }
      });
    });

    sizeOptions.forEach(function (opt) {
      opt.addEventListener('click', function () {
        var isByoOpt = opt.dataset.isByo === 'true';
        /* If clicking the already-active item, toggle expand/collapse */
        if (opt.classList.contains('pdp-ss__size-option--active')) {
          if (sizeList && getVisiblePackOptionCount() > 1) {
            var willOpen = !sizeList.classList.contains('pdp-ss__size-list--expanded');
            if (willOpen) {
              announceCloseAllDropdowns('size-list');
              closePdpDropdowns('size-list');
              announceDropdownOpened('size-list');
            }
            sizeList.classList.toggle('pdp-ss__size-list--expanded');
            if (willOpen) anchorToFlavorDropdown();
          }
          return;
        }

        if (!isOptionAvailable(opt)) {
          /* Sold-out: select the size so ATC updates to Notify Me, then collapse */
          handleSizeSelect(opt, { anchorTop: !isByoOpt, anchorFieldset: isByoOpt, fromUserClick: true });
          if (sizeList) sizeList.classList.remove('pdp-ss__size-list--expanded');
          return;
        }

        /* Selecting a new item — apply and collapse */
        handleSizeSelect(opt, { anchorTop: !isByoOpt, anchorFieldset: isByoOpt, fromUserClick: true });
        if (sizeList) sizeList.classList.remove('pdp-ss__size-list--expanded');
      });
    });

    /* Auto-select first size of the active format on initial load */
    var activeFormat = container.dataset.currentFormat;
    if (activeFormat) {
      selectFirstInFormat(activeFormat);
    }
    if (!container.querySelector('.pdp-ss__size-option--active')) {
      var firstAvailableBtn = Array.from(container.querySelectorAll('.pdp-ss__format-btn')).find(function (btn) {
        return btn.dataset.available !== 'false' && !btn.disabled;
      });
      if (firstAvailableBtn) firstAvailableBtn.click();
    }
    /* Sync variant-scoped ingredients/facts on initial load. For BYO, also
       force the gallery so icons/media reflect the builder product. */
    var initActiveOpt = container.querySelector('.pdp-ss__size-option--active');
    if (initActiveOpt) {
      updateIngredientsSection(
        initActiveOpt.dataset.productId,
        initActiveOpt.dataset.isByo === 'true'
          ? initActiveOpt.dataset.mediaVariantId || initActiveOpt.dataset.variantId
          : initActiveOpt.dataset.variantId,
        getOptionServingCount(initActiveOpt),
        initActiveOpt.dataset.featuredImageUrl || ''
      );
      if (initActiveOpt.dataset.isByo === 'true') {
        currentGalleryProductId = null;
        updateGallery(initActiveOpt.dataset.mediaProductId || initActiveOpt.dataset.productId, true);
      }
    }

    if (defaultByoOnLoad) {
      var defaultByoOpt = container.querySelector('.pdp-ss__size-option[data-is-byo="true"]');
      if (defaultByoOpt && !defaultByoOpt.classList.contains('pdp-ss__size-option--active')) {
        handleSizeSelect(defaultByoOpt, { anchorFieldset: true });
      }
    }
    clearByoDefaultIntent();
    syncPackExpanderState();
  }

  /* ── Unified Dropdown Mode ── */
  if (mode === 'unified_dropdown') {
    var trigger = container.querySelector('.pdp-ss__dropdown-trigger');
    var panel = container.querySelector('.pdp-ss__dropdown-panel');
    var options = container.querySelectorAll('.pdp-ss__dropdown-option');
    unifiedTrigger = trigger;
    unifiedPanel = panel;

    if (panel && options && options.length) {
      var optionList = Array.from(options);
      var originalIndex = new Map();
      optionList.forEach(function (opt, idx) { originalIndex.set(opt, idx); });

      function getServingsCount(opt) {
        var source = [
          opt.dataset.option1 || '',
          opt.dataset.packageName || '',
          opt.querySelector('.pdp-ss__size-name') ? opt.querySelector('.pdp-ss__size-name').textContent : ''
        ].join(' ').toLowerCase();
        var m = source.match(/(\d+)\s*serv/);
        return m ? parseInt(m[1], 10) : 0;
      }

      function getGroupRank(opt) {
        if (opt.dataset.isByo === 'true') return 2; /* BYO always last */
        var source = [
          opt.dataset.packageName || '',
          opt.dataset.option1 || '',
          opt.querySelector('.pdp-ss__size-name') ? opt.querySelector('.pdp-ss__size-name').textContent : ''
        ].join(' ').toLowerCase();
        if (source.indexOf('bag') !== -1) return 0;
        if (source.indexOf('pack') !== -1) return 1;
        return 1; /* non-bag defaults with packs */
      }

      optionList.sort(function (a, b) {
        var rankA = getGroupRank(a);
        var rankB = getGroupRank(b);
        if (rankA !== rankB) return rankA - rankB;

        /* Within bags, highest servings first */
        if (rankA === 0) {
          var servingsA = getServingsCount(a);
          var servingsB = getServingsCount(b);
          if (servingsA !== servingsB) return servingsB - servingsA;
        }

        return (originalIndex.get(a) || 0) - (originalIndex.get(b) || 0);
      });

      optionList.forEach(function (opt) { panel.appendChild(opt); });
    }

    /* Single-size BMSM panels retain variant data without opening a size menu. */
    if (trigger && panel && !panel.hidden) {
      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (!isOpen) {
          announceCloseAllDropdowns('size-dropdown');
          closePdpDropdowns('size-dropdown');
          announceDropdownOpened('size-dropdown');

          /* Anchor to Select a Size + small lead-in when opening */
          var selectSizeEl = container.querySelector('.select-a-size') || document.querySelector('.select-a-size');
          if (selectSizeEl) {
            var stickyOffset = 0;
            if (document.body && document.body.classList.contains('is-header-sticky')) {
              var stickyHeader = document.querySelector('.ae--header');
              stickyOffset = stickyHeader ? (stickyHeader.offsetHeight || 0) : 0;
            }
            var infoContainer = selectSizeEl.closest('.ae--product-information') || document.querySelector('.ae--product-information');
            if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight + 1) {
              var cRect = infoContainer.getBoundingClientRect();
              var tRect = selectSizeEl.getBoundingClientRect();
              var localTop = infoContainer.scrollTop + (tRect.top - cRect.top) - 12;
              infoContainer.scrollTo({ top: Math.max(localTop, 0), behavior: 'smooth' });
            }
            var isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
            if (isMobile) {
              var wTop = selectSizeEl.getBoundingClientRect().top + window.pageYOffset - stickyOffset - 12;
              window.scrollTo({ top: Math.max(wTop, 0), behavior: 'smooth' });
            }
          }
        }
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.style.display = isOpen ? 'none' : 'block';
      });
    }

    if (options) {
      options.forEach(function (opt) {
        opt.addEventListener('click', function () {
          var isByoOpt = opt.dataset.isByo === 'true';
          if (!isOptionAvailable(opt)) {
            /* Sold-out: select the size so ATC updates to Notify Me, then close panel */
            handleSizeSelect(opt, { anchorFieldset: isByoOpt, fromUserClick: true });
            if (isByoOpt) {
              var byoFieldset = opt.closest('.pdp-ss__fieldset') || container.querySelector('.pdp-ss__fieldset');
              if (byoFieldset) anchorToFieldsetTop(byoFieldset);
            } else {
              var isMobileSO = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
              if (isMobileSO) {
                anchorToSelectASize();
              } else {
                var flavorsEl = document.querySelector('.ae--product-flavors');
                if (flavorsEl) anchorToFieldsetTop(flavorsEl, 16);
              }
            }
            return;
          }
          handleSizeSelect(opt, { anchorFieldset: isByoOpt, fromUserClick: true });
          if (isByoOpt) {
            var byoFieldset2 = opt.closest('.pdp-ss__fieldset') || container.querySelector('.pdp-ss__fieldset');
            if (byoFieldset2) anchorToFieldsetTop(byoFieldset2);
          } else {
            var isBmsmOpt2 = opt.dataset.isBmsmTagged === 'true';
            var isMobile2 = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
            if (isMobile2 && isBmsmOpt2) {
              anchorToSelectASize();
              window.setTimeout(anchorToSelectASize, 100);
            } else {
              var flavorsEl2 = document.querySelector('.ae--product-flavors');
              if (flavorsEl2) anchorToFieldsetTop(flavorsEl2, 16);
            }
          }
        });
      });
    }

    /* Sync variant-scoped ingredients/facts on initial load. For BYO, also
       force the gallery so icons/media reflect the builder product. */
    var initActiveDropOpt = container.querySelector('.pdp-ss__dropdown-option--active');
    if (initActiveDropOpt) {
      updateIngredientsSection(
        initActiveDropOpt.dataset.productId,
        initActiveDropOpt.dataset.isByo === 'true'
          ? initActiveDropOpt.dataset.mediaVariantId || initActiveDropOpt.dataset.variantId
          : initActiveDropOpt.dataset.variantId,
        getOptionServingCount(initActiveDropOpt),
        initActiveDropOpt.dataset.featuredImageUrl || ''
      );
      if (initActiveDropOpt.dataset.isByo === 'true') {
        currentGalleryProductId = null;
        updateGallery(initActiveDropOpt.dataset.mediaProductId || initActiveDropOpt.dataset.productId, true);
      }
    }

    if (defaultByoOnLoad) {
      var defaultByoDropOpt = container.querySelector('.pdp-ss__dropdown-option[data-is-byo="true"]');
      if (defaultByoDropOpt && !defaultByoDropOpt.classList.contains('pdp-ss__dropdown-option--active')) {
        handleSizeSelect(defaultByoDropOpt, { anchorFieldset: true });
      }
    }
    clearByoDefaultIntent();

    /* Show/hide subscribe toggle based on the initially active option */
    (function initRcToggleVisibility() {
      var rcPanelInit = document.getElementById('rc-toggle-panel');
      if (!rcPanelInit) return;
      var activeDropOpt = container.querySelector('.pdp-ss__dropdown-option--active');
      if (!activeDropOpt) return;
      var plansJson = activeDropOpt.getAttribute('data-selling-plans');
      var hasPlans = false;
      if (plansJson) {
        try { hasPlans = JSON.parse(plansJson).length > 0; } catch (e) { /* ignore */ }
      }
      if (activeDropOpt.dataset.isByo === 'true') hasPlans = false;
      if (activeDropOpt.dataset.isBmsmTagged === 'true') hasPlans = false;
      if (activeDropOpt.dataset.available === 'false') hasPlans = false;
      rcPanelInit.style.display = hasPlans ? '' : 'none';
    })();
  }

  /* ── BYO inline helpers ── */
  function initByoBuilder(byoEl) {
    var key = byoEl.dataset.byoFormat || 'default';
    if (byoInitialized[key]) return;
    if (typeof BYOBuilder !== 'undefined') {
      var byoRoot = byoEl.querySelector('.byo-builder');
      if (byoRoot && !byoRoot._byoInstance) {
        byoRoot._byoInstance = new BYOBuilder(byoRoot);
      }
    }
    byoInitialized[key] = true;
  }

  function showByo(opt) {
    var byoEl = null;

    if (mode === 'format_split') {
      var fmtVal = opt.dataset.format;
      byoInlines.forEach(function (byo) {
        if (byo.dataset.byoFormat === fmtVal) byoEl = byo;
      });
    } else {
      if (byoInlines.length > 0) byoEl = byoInlines[0];
    }

    if (!byoEl) {
      console.warn('[BYO] No matching byoEl found — showByo returning false');
      return false;
    }

    byoEl.style.display = '';
    initByoBuilder(byoEl);
    /* Force the sticky-header state so the anchor + panel budget are consistent
       whether or not the user has scrolled yet. Otherwise the anchor lands 119px
       higher than intended on load and the panel appears not to fill the space. */
    if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
      document.body.classList.add('is-header-sticky');
    }
    document.dispatchEvent(new CustomEvent('pdp:layout-changed'));

    /* Re-render the existing BYO instance so the PDP ATC button label reflects
       the current selection count (not the stale "Add to Cart" from prior size). */
    var byoRootEl = byoEl.querySelector('.byo-builder');
    if (byoRootEl && byoRootEl._byoInstance) {
      byoRootEl._byoInstance.render();
    }

    /* On mobile, anchor to select-a-size and fit BYO between it and the ATC wrap. */
    if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {

      var fitByoInViewport = function () {
        var byoRoot = byoEl.querySelector('.byo-builder');
        if (!byoRoot) {
          console.warn('[BYO] fitByoInViewport — no .byo-builder found inside byoEl');
          return;
        }
        var stickyControls = byoRoot.querySelector('.byo-sticky');

        var stickyAtcWrap = document.querySelector('.pdp-atc-wrap');
        var stickyAtcHeight = stickyAtcWrap ? (stickyAtcWrap.offsetHeight + 32) : 0;

        var byoListEl = byoRoot.querySelector('.byo-builder__list');
        if (byoListEl) {
          var byoListRect = byoListEl.getBoundingClientRect();
          var available = window.innerHeight - byoListRect.top - stickyAtcHeight - 16;
          if (available > 80) {
            byoEl.classList.add('pdp-ss__byo-inline--constrained');
            /* BYOBuilder caps the outer .byo-builder--list itself; keep the inner list uncapped. */
            byoListEl.style.maxHeight = '';
            byoListEl.style.overflowY = '';
            if (stickyControls) {
              stickyControls.style.top = '0px';
            }
          } else {
            byoEl.classList.remove('pdp-ss__byo-inline--constrained');
            byoListEl.style.maxHeight = '';
            byoListEl.style.overflowY = '';
            if (stickyControls) {
              stickyControls.style.top = '';
            }
          }
        }
      };

      /* Anchor + constrain together after layout settles to avoid jiggle */
      window.setTimeout(function () {
        anchorToSelectASize();
        window.requestAnimationFrame(function () {
          fitByoInViewport();
          window.requestAnimationFrame(function () {
            fitByoInViewport();
            anchorToSelectASize();
          });
        });
      }, 60);
      window.setTimeout(function () {
        fitByoInViewport();
        anchorToSelectASize();
      }, 200);
    }
    return true;
  }

  function hideAllByo() {
    byoInlines.forEach(function (byo) {
      byo.style.display = 'none';
      byo.classList.remove('pdp-ss__byo-inline--constrained');
      var byoRoot = byo.querySelector('.byo-builder');
      if (byoRoot) {
        byoRoot.style.maxHeight = '';
        byoRoot.style.overflowY = '';
        byoRoot.scrollTop = 0;
        var byoListEl = byoRoot.querySelector('.byo-builder__list');
        if (byoListEl) {
          byoListEl.style.maxHeight = '';
          byoListEl.style.overflowY = '';
        }
        var stickyControls = byoRoot.querySelector('.byo-sticky');
        if (stickyControls) {
          stickyControls.style.top = '';
        }
      }
    });
    document.dispatchEvent(new CustomEvent('pdp:layout-changed'));
  }

  /* Keep the sticky-header state enforced while any BYO inline is visible so
     the anchor + panel budget stay consistent even when the user scrolls back
     to the top (global.js's scroll listener would otherwise remove the class). */
  (function keepStickyWhileByoVisible() {
    if (!byoInlines.length) return;
    if (!('MutationObserver' in window)) return;
    var isMobileQ = window.matchMedia && window.matchMedia('(max-width: 767px)');
    var anyByoVisible = function () {
      for (var i = 0; i < byoInlines.length; i++) {
        var el = byoInlines[i];
        if (el.style.display !== 'none' && window.getComputedStyle(el).display !== 'none') return true;
      }
      return false;
    };
    var mo = new MutationObserver(function () {
      if (!isMobileQ || !isMobileQ.matches) return;
      if (!anyByoVisible()) return;
      if (!document.body.classList.contains('is-header-sticky')) {
        document.body.classList.add('is-header-sticky');
      }
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  })();

  function clearActiveSize() {
    container.querySelectorAll('.pdp-ss__size-option--active').forEach(function (el) {
      el.classList.remove('pdp-ss__size-option--active');
    });
  }

  /* Auto-select the best size option in a given format.
     Prefer the current page product's variant, then fall back to first available. */
  function selectFirstInFormat(format, anchorTop) {
    clearActiveSize();
    var candidates = Array.from(container.querySelectorAll('.pdp-ss__size-option[data-format="' + format + '"]:not([data-is-byo="true"])'));
    var pageVariantId = container.dataset.currentVariantId;

    /* Try the page product's own variant first, even if sold out. */
    var best = pageVariantId
      ? candidates.find(function (opt) {
          return opt.dataset.variantId === pageVariantId;
        })
      : null;

    /* Fall back to first available */
    if (!best) {
      best = candidates.find(function (opt) {
        return isOptionAvailable(opt);
      });
    }

    if (best) {
      handleSizeSelect(best, { anchorTop: !!anchorTop });
    }
  }

  /* ── Update selling plans when switching to a different product ── */
  function updateSellingPlans(opt) {
    var sellingPlanInput = document.getElementById('selling-plan-input');
    var rcFreqSelect = document.getElementById('rc-freq-select');
    var rcToggleBtn = document.getElementById('rc-toggle-btn');
    if (!sellingPlanInput || !rcFreqSelect) return;

    /* Parse selling plans from the size option */
    var plansJson = opt.getAttribute('data-selling-plans');
    var plans = [];
    if (plansJson) {
      try { plans = JSON.parse(plansJson); } catch (e) { /* ignore */ }
    }

    /* Repopulate the frequency dropdown with the new product's plans */
    if (plans.length > 0) {
      rcFreqSelect.innerHTML = '';
      plans.forEach(function (plan) {
        var option = document.createElement('option');
        option.value = plan.id;
        /* Strip "save X%" suffix from plan name */
        var text = plan.name.replace(/:\s*save\s+\d+%/i, '').trim();
        option.textContent = text;
        rcFreqSelect.appendChild(option);
      });

      /* Update the hidden selling_plan input if subscription is active */
      var isSubscribed = rcToggleBtn && rcToggleBtn.getAttribute('aria-expanded') === 'true';
      if (isSubscribed) {
        sellingPlanInput.value = rcFreqSelect.value;
      }
    }
  }

  /* ── Update page price + variant ── */
  function updatePageVariant(opt, isByoForced) {
    var variantId = opt.dataset.variantId;
    var price = parseInt(opt.dataset.price, 10);
    var compareAt = parseInt(opt.dataset.compareAtPrice, 10) || 0;
    var packageName = opt.dataset.packageName || '';
    var productId = opt.dataset.productId || '';
     var mediaProductId = opt.dataset.mediaProductId || productId;
     var mediaVariantId = opt.dataset.mediaVariantId || '';
    var isAvailable = isOptionAvailable(opt);

    /* Read current quantity from the qty selector */
    var qtyInput = document.querySelector('.ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"]');
    var currentQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    /* Swap gallery images if switching to a different product;
       force-refresh for BYO so icons/gallery always update. */
    var featuredImageUrl = opt.dataset.featuredImageUrl || '';
    var servingCount = getOptionServingCount(opt);
    updateGallery(mediaProductId, !!isByoForced, mediaVariantId, featuredImageUrl);
    updateIngredientsSection(productId, isByoForced ? mediaVariantId : variantId, servingCount, featuredImageUrl);

    /* Update EA auto-discount badge: sync the price-container's data attrs
       so the CSS rules from section-ea-discounts can activate/deactivate the badge. */
    var priceContainer = document.querySelector('.price-container[data-ea-product-handle]');
    if (priceContainer) {
      var newHandle = opt.dataset.productHandle || '';
      var newTags = opt.dataset.productTags || '';
      priceContainer.setAttribute('data-ea-product-handle', newHandle);
      priceContainer.setAttribute('data-ea-tags', newTags);
      priceContainer.setAttribute('data-ea-variant-id', variantId);
    }

    /* EA pricing caches applied elements. Replace the selected variant's source
       data and clear that cache before asking it to recalculate the PDP. */
    if (priceEl) {
      priceEl.setAttribute('data-product-id', productId);
      priceEl.setAttribute('data-variant-id', variantId);
      priceEl.setAttribute('data-original-price', price);
      priceEl.removeAttribute('data-ea-applied');
    }

    /* Update hidden variant select in the product form.
       If the variant belongs to a sibling product, add a temporary option
       so the form can submit the correct ID. */
    if (variantSelect) {
      var existingOption = variantSelect.querySelector('option[value="' + variantId + '"]');
      if (!existingOption) {
        /* Remove any previously-added cross-product option */
        var prev = variantSelect.querySelector('option[data-cross-product]');
        if (prev) prev.remove();
        /* Add the sibling variant as a temporary option */
        var newOpt = document.createElement('option');
        newOpt.value = variantId;
        newOpt.dataset.price = price;
        newOpt.dataset.compareAtPrice = compareAt;
        newOpt.dataset.crossProduct = 'true';
        newOpt.dataset.available = isAvailable ? 'true' : 'false';
        newOpt.textContent = packageName;
        variantSelect.appendChild(newOpt);
      }
      existingOption = existingOption || newOpt;
      if (existingOption) existingOption.dataset.available = isAvailable ? 'true' : 'false';
      variantSelect.value = variantId;
      /* Trigger change event for any listeners */
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Update selling plan for the new variant's product */
    updateSellingPlans(opt);

    /* Update displayed price */
    if (priceEl && typeof window.formatMoney === 'function') {
      priceEl.innerHTML = window.formatMoney(price, fmt);
    }

    /* Keep compare block non-sale for fixed per-unit PDP display. */
    if (compareEl && breaksEl) {
      compareEl.classList.remove('sale');
      breaksEl.classList.remove('sale');
    }

    /* Update ATC button price and compare-at */
    var atcPrice = document.querySelector('[data-atc-price]');
    var atcCompare = document.querySelector('[data-atc-compare]');
    var atcBtn = document.querySelector('[data-pdp-atc]');
    var notifyBtn = document.querySelector('[data-pdp-notify]');

    /* Reset ATC label to "Add to Cart" whenever a non-BYO size is selected */
    var isByoOpt = opt.dataset.isByo === 'true';
    var atcLabel = atcBtn ? atcBtn.querySelector('.pdp-atc-btn__label') : null;
    if (atcLabel && !isByoOpt) atcLabel.textContent = 'Add to Cart';

    /* Keep the URL's variant param in sync, and clear BYO flavor params from
       the URL when selecting a non-BYO size. */
    var urlParams = new URLSearchParams(window.location.search);
    if (variantId) {
      urlParams.set('variant', variantId);
    }
    if (!isByoOpt) {
      urlParams.delete('flavors');
      urlParams.delete('bundle-qty');
    }
    var qs = urlParams.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);

    /* Show/hide the two buttons based on availability */
    if (atcBtn) {
      atcBtn.disabled = false;
      if (isAvailable) {
        atcBtn.classList.remove('pdp-atc-btn--hidden');
      } else {
        atcBtn.classList.add('pdp-atc-btn--hidden');
      }
    }
    if (notifyBtn) {
      notifyBtn.setAttribute('data-variant-id', variantId);
      notifyBtn.setAttribute('data-cse-variant', variantId);
      var notifyHandle = String(opt.dataset.productUrl || '').split('?')[0].split('/products/')[1];
      if (notifyHandle) notifyBtn.setAttribute('data-cse-product', notifyHandle.split('/')[0]);
      if (!isAvailable) {
        notifyBtn.classList.remove('pdp-atc-btn--hidden');
      } else {
        notifyBtn.classList.add('pdp-atc-btn--hidden');
      }
    }

    syncQtyVisibilityForNotify(isAvailable);

    if (typeof window.formatMoney === 'function') {
      /* Check if subscribe toggle is active */
      var rcToggle = document.getElementById('rc-toggle-btn');
      var isSubscribed = rcToggle && rcToggle.getAttribute('aria-expanded') === 'true';
      var displayPrice = price;
      var showCompare = false;
      var compareDisplay = compareAt;

      if (isSubscribed) {
        /* Apply 15% subscription discount */
        displayPrice = Math.round(price * 0.85);
        showCompare = true;
        /* If automatic discount exists, show original compare-at; otherwise show sale price */
        compareDisplay = (compareAt > price) ? compareAt : price;
      } else if (compareAt > price) {
        showCompare = true;
        compareDisplay = compareAt;
      }

      if (atcPrice) atcPrice.textContent = window.formatMoney(displayPrice * currentQty, fmt);

      if (atcCompare) {
        if (showCompare) {
          atcCompare.textContent = window.formatMoney(compareDisplay * currentQty, fmt);
          atcCompare.style.display = 'inline';
        } else {
          atcCompare.style.display = 'none';
        }
      }
    }

    /* Store current variant data for subscribe toggle to recalculate */
    window._pdpCurrentVariant = { price: price, compareAt: compareAt };

    /* Keep the subscribe panel price row in sync with the selected variant */
    if (typeof window.updateRcTogglePrices === 'function') {
      window.updateRcTogglePrices(price * currentQty);
    }

    document.dispatchEvent(new CustomEvent('pdp:variant-change', {
      bubbles: true,
      detail: {
        variantId: variantId,
        productId: productId,
        price: price,
        compareAtPrice: compareAt,
        productHandle: opt.dataset.productHandle || '',
        productTags: opt.dataset.productTags || ''
      }
    }));
  }

  /* ── Update dropdown trigger text (unified mode) ── */
  function updateDropdownTrigger(opt) {
    if (mode !== 'unified_dropdown') return;
    var ddTrigger = container.querySelector('.pdp-ss__dropdown-trigger');
    if (!ddTrigger) return;

    var selectedInfo = ddTrigger.querySelector('.pdp-ss__dropdown-selected');
    if (selectedInfo) {
      /* Update thumbnail */
      var thumbEl = selectedInfo.querySelector('.pdp-ss__dropdown-thumb');
      var newThumb = opt.dataset.thumbnail;
      if (thumbEl && newThumb) {
        thumbEl.src = newThumb;
        thumbEl.alt = opt.querySelector('.pdp-ss__size-name') ? opt.querySelector('.pdp-ss__size-name').textContent : '';
      }
      var nameEl = selectedInfo.querySelector('.pdp-ss__size-name');
      var ppsEl = selectedInfo.querySelector('.pdp-ss__size-pps');
      var priceElT = selectedInfo.querySelector('.pdp-ss__size-price');
      var optName = opt.querySelector('.pdp-ss__size-name');
      var optPps = opt.querySelector('.pdp-ss__size-pps');
      var optPrice = opt.querySelector('.pdp-ss__size-price');
      if (nameEl && optName) nameEl.textContent = optName.textContent;
      if (ppsEl && optPps) ppsEl.textContent = optPps.textContent;
      else if (ppsEl && !optPps) ppsEl.textContent = '';
      if (priceElT && optPrice) {
        priceElT.textContent = optPrice.textContent;
        if (optPrice.classList.contains('pdp-ss__sold-out-text')) {
          priceElT.classList.add('pdp-ss__sold-out-text');
        } else {
          priceElT.classList.remove('pdp-ss__sold-out-text');
        }
      }
    }
  }

  /* ── Shared: handle size option click ── */
  function handleSizeSelect(opt, config) {
    config = config || {};
    var isByo = opt.dataset.isByo === 'true';
    setByoSelectedState(isByo);
    syncSizeBmsmBadgeForOption(opt);

    if (config.anchorFieldset) {
      var fieldset = opt.closest('.pdp-ss__fieldset') || container.querySelector('.pdp-ss__fieldset');
      if (fieldset) anchorToFieldsetTop(fieldset);
    }

    if (config.anchorTop) {
      var isBmsmOpt = opt.dataset.isBmsmTagged === 'true';
      var isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      if (isMobile && isBmsmOpt) {
        anchorToSelectASize();
        window.setTimeout(anchorToSelectASize, 100);
      } else {
        var flavorsEl = document.querySelector('.ae--product-flavors');
        if (flavorsEl) {
          anchorToFieldsetTop(flavorsEl, 16);
        } else {
          queueAnchorToPageTop();
        }
      }
    }

    /* Reset subscription toggle to off whenever a size/format is selected */
    (function resetSubscriptionToggle() {
      var toggleBtn = document.getElementById('rc-toggle-btn');
      if (toggleBtn && toggleBtn.getAttribute('aria-expanded') === 'true') {
        toggleBtn.setAttribute('aria-expanded', 'false');
        var details = document.getElementById('rc-toggle-details');
        if (details) details.classList.remove('rc-toggle-panel__details--open');
        var spInput = document.getElementById('selling-plan-input');
        if (spInput) spInput.value = '';
      }
    })();

    /* Update active states for all options */
    var allOpts = container.querySelectorAll('.pdp-ss__size-option, .pdp-ss__dropdown-option');
    allOpts.forEach(function (o) {
      o.classList.remove('pdp-ss__size-option--active', 'pdp-ss__dropdown-option--active', 'pdp-ss__size-option--preview');
    });
    opt.classList.add(
      opt.classList.contains('pdp-ss__dropdown-option') ? 'pdp-ss__dropdown-option--active' : 'pdp-ss__size-option--active'
    );

    /* Update the page price + hidden variant */
    updatePageVariant(opt, isByo);

    /* Notify flavor dropdown (styled section only) that a new size was selected */
    document.dispatchEvent(new CustomEvent('pdp:size-selected', {
      bubbles: true,
      detail: {
        option1: opt.dataset.option1 || '',
        option2: opt.dataset.option2 || '',
        option3: opt.dataset.option3 || '',
        format: opt.dataset.format || '',
        packageName: opt.dataset.packageName || '',
        variantId: opt.dataset.variantId || ''
      }
    }));

    if (isByo) {
      showByo(opt);

      /* Hide subscribe panel for BYO selections and ensure toggle is off */
      var rcPanel = document.getElementById('rc-toggle-panel');
      if (rcPanel) rcPanel.style.display = 'none';
      var rcToggleByo = document.getElementById('rc-toggle-btn');
      if (rcToggleByo) {
        rcToggleByo.setAttribute('aria-expanded', 'false');
        var rcDetailsByo = document.getElementById('rc-toggle-details');
        if (rcDetailsByo) rcDetailsByo.classList.remove('rc-toggle-panel__details--open');
        var spInputByo = document.getElementById('selling-plan-input');
        if (spInputByo) spInputByo.value = '';
      }

      /* Close dropdown panel if in unified mode */
      if (mode === 'unified_dropdown') {
        var ddTrigger = container.querySelector('.pdp-ss__dropdown-trigger');
        var ddPanel = container.querySelector('.pdp-ss__dropdown-panel');
        if (ddTrigger) ddTrigger.setAttribute('aria-expanded', 'false');
        if (ddPanel) ddPanel.style.display = 'none';
        updateDropdownTrigger(opt);
      }

    } else {
      /* Non-BYO: hide BYO builder */
      hideAllByo();

      /* Show subscribe panel only if the selected product has selling plans and is not BMSM */
      var rcPanel2 = document.getElementById('rc-toggle-panel');
      if (rcPanel2) {
        var plansJson = opt.getAttribute('data-selling-plans');
        var hasPlans = false;
        if (plansJson) {
          try { hasPlans = JSON.parse(plansJson).length > 0; } catch (e) { /* ignore */ }
        }
        if (!isOptionAvailable(opt)) hasPlans = false;
        if (opt.dataset.isBmsmTagged === 'true') hasPlans = false;
        rcPanel2.style.display = hasPlans ? '' : 'none';

        /* If hiding, also reset the toggle state and clear the selling plan input */
        if (!hasPlans) {
          var toggleBtn = document.getElementById('rc-toggle-btn');
          if (toggleBtn && toggleBtn.getAttribute('aria-expanded') === 'true') {
            toggleBtn.setAttribute('aria-expanded', 'false');
            var details = document.getElementById('rc-toggle-details');
            if (details) details.classList.remove('rc-toggle-panel__details--open');
          }
          var spInput = document.getElementById('selling-plan-input');
          if (spInput) spInput.value = '';
        }
      }

      /* Close dropdown panel if in unified mode */
      if (mode === 'unified_dropdown') {
        var ddTrigger2 = container.querySelector('.pdp-ss__dropdown-trigger');
        var ddPanel2 = container.querySelector('.pdp-ss__dropdown-panel');
        if (ddTrigger2) ddTrigger2.setAttribute('aria-expanded', 'false');
        if (ddPanel2) ddPanel2.style.display = 'none';
        updateDropdownTrigger(opt);
      }
    }

    bmsmRefreshVisibility();

  }

  /* Liquid's product form can fall back to the first available variant even
     when the URL explicitly selects a sold-out variant. Reconcile the form
     and purchase controls with the selector's active option on first load. */
  (function syncInitialSelectedVariant() {
    var activeOpt = getActiveSizeOption();
    if (!activeOpt || activeOpt.dataset.isByo === 'true') return;

    var activeVariantId = String(activeOpt.dataset.variantId || '');
    var formVariantId = variantSelect ? String(variantSelect.value || '') : '';
    var activeAvailable = isOptionAvailable(activeOpt);
    var atcBtn = document.querySelector('[data-pdp-atc]');
    var notifyBtn = document.querySelector('[data-pdp-notify]');
    var atcVisible = !!(atcBtn && !atcBtn.classList.contains('pdp-atc-btn--hidden'));
    var notifyVisible = !!(notifyBtn && !notifyBtn.classList.contains('pdp-atc-btn--hidden'));
    var controlsMismatch = activeAvailable
      ? (!atcVisible || notifyVisible)
      : (atcVisible || !notifyVisible);

    if (activeVariantId && (formVariantId !== activeVariantId || controlsMismatch)) {
      updatePageVariant(activeOpt, false);
    }
  })();

  (function syncInitialQtyVisibility() {
    var atcBtn = document.querySelector('[data-pdp-atc]');
    var notifyBtn = document.querySelector('[data-pdp-notify]');
    if (!atcBtn || !notifyBtn) return;

    var notifyVisible = !notifyBtn.classList.contains('pdp-atc-btn--hidden');
    var atcHidden = atcBtn.classList.contains('pdp-atc-btn--hidden');
    syncQtyVisibilityForNotify(!(notifyVisible && atcHidden));
  })();

  bmsmInitInteractions();
  bmsmRefreshVisibility();
  syncSizeBmsmBadgeForOption(initActiveOpt || getActiveSizeOption());

  /* Anchor to "Select a Size" on any engagement inside BMSM or BYO panels */
  (function bindPanelEngagementAnchors() {
    var bmsmEl = container.querySelector('.pdp-ss__bmsm');
    var byoBuilderEl = document.querySelector('.byo-builder');

    function anchorAfterEngagement() {
      window.requestAnimationFrame(function () { anchorToSelectASize(); });
    }

    if (bmsmEl) bmsmEl.addEventListener('click', anchorAfterEngagement);
    if (byoBuilderEl) byoBuilderEl.addEventListener('click', anchorAfterEngagement);
  })();

  (function bindBmsmSubscriptionPriceSync() {
    var rcToggleBtn = document.getElementById('rc-toggle-btn');
    var rcFreqSelect = document.getElementById('rc-freq-select');
    if (rcToggleBtn) {
      rcToggleBtn.addEventListener('click', function () {
        window.setTimeout(function () {
          if (bmsmState.active) bmsmUpdateAtcPrice();
        }, 0);
      });
    }
    if (rcFreqSelect) {
      rcFreqSelect.addEventListener('change', function () {
        if (bmsmState.active) bmsmUpdateAtcPrice();
      });
    }
  })();

  function bmsmSubmitBundleFromForm(form) {
    if (!bmsmPanel) return Promise.resolve(false);
    var isPanelVisible = window.getComputedStyle(bmsmPanel).display !== 'none';
    if (!isPanelVisible) return Promise.resolve(false);

    var domQuantities = bmsmCollectQuantitiesFromDom();
    var items = bmsmBuildAddItems(domQuantities);
    if (!items.length) return Promise.resolve(false);
    if (bmsmState.submitting) return Promise.resolve(true);

    bmsmState.submitting = true;

    function bmsmRequestAdd(addItems) {
      return window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: addItems })
      })
        .then(function (response) {
          if (!response.ok) {
            var error = new Error('Failed to add BMSM bundle');
            error.status = response.status;
            throw error;
          }
          return response.json();
        });
    }

    function bmsmAddSequential(addItems) {
      var successCount = 0;
      return addItems.reduce(function (chain, item) {
        return chain.then(function () {
          return bmsmRequestAdd([item])
            .then(function () {
              successCount += 1;
            })
            .catch(function (error) {
              console.error(error);
            });
        });
      }, Promise.resolve())
        .then(function () {
          if (!successCount) throw new Error('Sequential BMSM add failed');
        });
    }

    var hadSellingPlan = items.some(function (item) { return !!item.selling_plan; });
    var strippedItems = hadSellingPlan ? bmsmStripSellingPlan(items) : items;

    return bmsmRequestAdd(items)
      .catch(function (error) {
        if (!hadSellingPlan) throw error;
        if (error && error.status !== 422) throw error;
        return bmsmRequestAdd(strippedItems);
      })
      .catch(function () {
        return bmsmAddSequential(strippedItems);
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('CartUpdated', { bubbles: true }));
        window.location.href = '/cart';
        return true;
      })
      .catch(function (error) {
        console.error(error);
        return false;
      })
      .finally(function () {
        bmsmState.submitting = false;
      });
  }

  function bmsmShouldHandleBundle(form) {
    if (!form || !bmsmPanel) return false;
    var isPanelVisible = window.getComputedStyle(bmsmPanel).display !== 'none';
    if (!isPanelVisible) return false;
    var domQuantities = bmsmCollectQuantitiesFromDom();
    var items = bmsmBuildAddItems(domQuantities);
    return items.length > 0;
  }

  function getVisibleByoBuilder() {
    var byoInline = document.querySelector('.pdp-ss__byo-inline:not([style*="display:none"]):not([style*="display: none"])');
    if (!byoInline || byoInline.offsetParent === null) return null;
    var byoRoot = byoInline.querySelector('.byo-builder');
    if (!byoRoot || !byoRoot.__byoBuilder) return null;
    return byoRoot.__byoBuilder;
  }

  document.addEventListener('click', function (event) {
    if (bmsmBypassInterceptOnce) {
      bmsmBypassInterceptOnce = false;
      return;
    }

    var addBtn = event.target.closest('.js-go-cart-add-to-cart, [data-pdp-atc]');
    if (!addBtn) return;
    var form = addBtn.closest('form.add-to-cart-styled, form.add-to-cart-identifier');
    if (!form) return;
    if (bmsmShouldHandleBundle(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      bmsmSubmitBundleFromForm(form);
      return;
    }

    var byoBuilder = getVisibleByoBuilder();
    if (!byoBuilder) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    byoBuilder.addToCart();
  }, true);

  document.addEventListener('submit', function (event) {
    if (bmsmBypassInterceptOnce) {
      bmsmBypassInterceptOnce = false;
      return;
    }

    var form = event.target;
    if (!form || form.nodeName !== 'FORM') return;
    if (!form.classList.contains('add-to-cart-styled') && !form.classList.contains('add-to-cart-identifier')) return;
    if (bmsmShouldHandleBundle(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      bmsmSubmitBundleFromForm(form);
      return;
    }

    var byoBuilder = getVisibleByoBuilder();
    if (!byoBuilder) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    byoBuilder.addToCart();
  }, true);

  /* ── On load: if ?flavors= is present in the URL, activate the BYO section ── */
  (function activateByoFromUrl() {
    var params = new URLSearchParams(window.location.search);
    if (!params.get('flavors')) return;

    if (mode === 'format_split') {
      var byoOpt = container.querySelector('.pdp-ss__size-option[data-is-byo="true"]');
      if (!byoOpt) return;

      /* Switch to the format tab that owns this BYO option */
      var byoFormat = byoOpt.dataset.format;
      if (byoFormat) {
        var matchingFormatBtn = Array.from(container.querySelectorAll('.pdp-ss__format-btn'))
          .find(function (b) { return b.dataset.format === byoFormat; });
        if (matchingFormatBtn) matchingFormatBtn.click(); /* non-trusted → no anchor scroll */
      }
      /* Ensure the BYO row is visible regardless of format-hide logic */
      byoOpt.style.display = '';
      handleSizeSelect(byoOpt);

    } else if (mode === 'unified_dropdown') {
      var byoDropOpt = container.querySelector('.pdp-ss__dropdown-option[data-is-byo="true"]');
      if (!byoDropOpt) return;
      handleSizeSelect(byoDropOpt);
    }
  })();

})();
